import { useInfiniteQuery } from "@tanstack/react-query";
import queryString from "query-string";
import { useEffect } from "react";
import Api from "../../api";
import StudentTag, { StudentTagType } from "../../domains/StudentTag";
import { createError, HTTPErrors } from "../../errors";
import { getNextPageParam } from "../../helpers/ReactQueryHelper";
import ApiResponse from "../../interfaces/ApiResponse";
import { useBoolean } from "../useBoolean";

type UseFetchTagsProps = {
  sectionId: string;
  params: FetchTagsParams;
};

type FetchTagsParams = {
  types: readonly StudentTagType[];
};

type ResponseBody = ApiResponse<StudentTag>;

export const useFetchAllTags = ({ sectionId, params }: UseFetchTagsProps) => {
  const completeFetchAllTags = useBoolean();

  const queryParams = queryString.stringify(params, { arrayFormat: "bracket" });
  const url = `/api/v1/sections/${sectionId}/all_tags?${queryParams}`;

  const result = useInfiniteQuery<ResponseBody, HTTPErrors>({
    queryKey: [url],
    queryFn: async ({ pageParam }) => {
      const res = await Api.get(`${url}&page=${pageParam}`);
      if (res.ok) {
        const json = await res.json();
        return json.allTags as ResponseBody;
      }
      throw await createError(res);
    },
    initialPageParam: 1,
    getNextPageParam,
  });
  const data = result.data?.pages.flatMap((page) => page.data);

  // all_tags はページング前提のAPIなので、自動で最後のページまでリクエストし切る
  useEffect(() => {
    if (result.data) {
      const len = result.data.pages.length;
      const meta = result.data.pages[len - 1].meta;
      if (meta.totalPages > meta.currentPage) {
        result.fetchNextPage();
      } else {
        completeFetchAllTags.on();
      }
    }
  }, [result.data]);

  return { ...result, data, completeFetchAlltags: completeFetchAllTags.value };
};
